import type { ReactNode } from "react";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { Box } from "@mui/material";
import { useRecordContext } from "react-admin";
import { useCurrentPolicyData, getResolvedClaimsValues, getEffectiveDeductible } from "../hooks/useCurrentPolicyData";
import { LaserChipWithReview } from "@/components/common/DeductibleReviewFlag";
import {
  LaseredDeductibleChip,
  ContractLaserDeductibleChip,
  ContractExcludedChip,
} from "@/components/ui/mui/LaseredDeductibleChip";
import { FamilyDeductibleChip } from "@/components/ui/mui/FamilyDeductibleChip";
import type { Claimant } from "@/types";

export const SpecificDeductibleCell = () => {
  const record = useRecordContext<Claimant>();
  const { policy, policyId, policyData } = useCurrentPolicyData();

  // Feature #3502: a review hold, or a deductible only the server can resolve
  // (conditional trigger, modification, review decision), shows the server
  // amount. The branches below predate those sources and stay as they are.
  const effective = getEffectiveDeductible(policyData, policy);
  const serverOnlySource =
    effective.source === "CONDITIONAL" || effective.source === "MODIFICATION" || effective.source === "REVIEW_DECISION";
  if (effective.reviewPending || serverOnlySource) {
    return (
      <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
        <LaserChipWithReview
          pending={effective.reviewPending}
          claimantId={record?.id ? String(record.id) : undefined}
          policyId={policyId}
          claimantName={record?.claimant_name}
        >
          <LaseredDeductibleChip />
        </LaserChipWithReview>
        <span>{formatUSD(effective.amount)}</span>
      </Box>
    );
  }

  const isLasered = policyData?.is_lasered;
  const laseredDeductible = policyData?.lasered_attributes?.amt_lasered_deductible;
  const hasLaseredDeductible = isLasered && laseredDeductible;

  // Contract laser / full exclusion (Feature #5287). Mutually exclusive with each
  // other and, in practice, with straight laser.
  const isContractExcluded = policyData?.is_contract_excluded;
  const contractLaserDeductible = policyData?.amt_contract_lasered_deductible;
  const hasContractLaser = policyData?.is_contract_lasered && contractLaserDeductible;

  // Full exclusion — show the exclusion indicator alongside the base deductible.
  if (isContractExcluded) {
    const baseValue = policy?.specific_benefit?.is_specific_deductible
      ? policy?.specific_benefit?.specific_deductible
      : undefined;
    return (
      <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
        <ContractExcludedChip />
        <span>{baseValue ? formatUSD(baseValue) : "-"}</span>
      </Box>
    );
  }

  const familyCtx = record?.family_deductible_context;
  const isInFamily = !hasLaseredDeductible && !hasContractLaser && record?.subscriber_ref_id;

  // Family deductible — from API context
  if (isInFamily) {
    const familyDeductibleValue = familyCtx?.family_specific_deductible;

    return (
      <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
        <FamilyDeductibleChip isPending={record?.is_pending_link} />
        {familyDeductibleValue !== null && familyDeductibleValue !== undefined ? (
          <span>{formatUSD(familyDeductibleValue)}</span>
        ) : (
          <span>-</span>
        )}
      </Box>
    );
  }

  // Contract laser — show the contract-laser deductible value.
  if (hasContractLaser) {
    return (
      <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
        <ContractLaserDeductibleChip />
        <span>{formatUSD(contractLaserDeductible)}</span>
      </Box>
    );
  }

  // Individual specific deductible
  if (!policy?.specific_benefit?.is_specific_deductible) {
    return <span>-</span>;
  }

  const value = hasLaseredDeductible ? laseredDeductible : policy?.specific_benefit?.specific_deductible;

  if (!value) return <span>-</span>;

  return (
    <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
      {hasLaseredDeductible && <LaseredDeductibleChip />}
      <span>{formatUSD(value)}</span>
    </Box>
  );
};

export const AggregateDeductibleCell = () => {
  const record = useRecordContext<Claimant>();
  const { policy, policyId, policyData } = useCurrentPolicyData();

  // Check if the policy has aggregate deductible coverage
  if (!policy?.aggregate_benefit?.is_aggregate_deductible) {
    return <span>-</span>;
  }

  // Feature #3502: while a deductible review holds the claimant, the laser chips
  // here swap for the review flag, as in the specific-deductible cell.
  const withReview = (chip: ReactNode) => (
    <LaserChipWithReview
      pending={!!policyData?.is_deductible_review_pending}
      claimantId={record?.id ? String(record.id) : undefined}
      policyId={policyId}
      claimantName={record?.claimant_name}
    >
      {chip}
    </LaserChipWithReview>
  );

  const isLasered = policyData?.is_lasered;
  const laseredDeductible = policyData?.lasered_attributes?.amt_lasered_deductible;
  const hasLaseredDeductible = isLasered && laseredDeductible;

  // Full exclusion — show the exclusion indicator alongside the base deductible.
  if (policyData?.is_contract_excluded) {
    const baseValue = policy?.aggregate_benefit?.aggregate_deductible;
    return (
      <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
        <ContractExcludedChip />
        <span>{baseValue ? formatUSD(baseValue) : "-"}</span>
      </Box>
    );
  }

  // Contract laser — flag the indicator; aggregate deductible value is unchanged.
  if (policyData?.is_contract_lasered) {
    const value = policy?.aggregate_benefit?.aggregate_deductible;
    if (!value) return <span>-</span>;
    return (
      <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
        {withReview(<ContractLaserDeductibleChip />)}
        <span>{formatUSD(value)}</span>
      </Box>
    );
  }

  const value = hasLaseredDeductible ? laseredDeductible : policy?.aggregate_benefit?.aggregate_deductible;

  if (!value) return <span>-</span>;

  return (
    <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
      {hasLaseredDeductible && withReview(<LaseredDeductibleChip />)}
      <span>{formatUSD(value)}</span>
    </Box>
  );
};

/** Displays total paid amount from policy_data.claims */
export const TotalPaidCell = () => {
  const { policyData } = useCurrentPolicyData();
  const claimsValues = getResolvedClaimsValues(policyData);

  if (!claimsValues?.total_paid) return <span>-</span>;

  return <span>{formatUSD(claimsValues.total_paid)}</span>;
};

/** Displays spend over deductible — uses family aggregate when the claimant is on a family deductible */
export const SpendOverDeductibleCell = () => {
  const record = useRecordContext<Claimant>();
  const { policyData } = useCurrentPolicyData();
  const claimsValues = getResolvedClaimsValues(policyData);

  const familyCtx = record?.family_deductible_context;
  const hasFamilyDeductible = familyCtx && familyCtx.family_specific_deductible > 0;

  // Family: spend over deductible = family_total_spend - family_specific_deductible (floored at 0)
  if (hasFamilyDeductible) {
    const familyOverDeductible = Math.max(familyCtx.family_total_spend - familyCtx.family_specific_deductible, 0);
    if (familyOverDeductible === 0 && !familyCtx.is_family_breach) return <span>-</span>;
    return (
      <Box display="flex" alignItems="center" gap={0.75} justifyContent="flex-end">
        <FamilyDeductibleChip isPending={record?.is_pending_link} />
        <span>{formatUSD(familyOverDeductible)}</span>
      </Box>
    );
  }

  if (!claimsValues?.spend_over_deductible) return <span>-</span>;

  return <span>{formatUSD(claimsValues.spend_over_deductible)}</span>;
};

/** Displays open (non-completed/cancelled) tasks — live count from handler enrichment */
export const OpenTasksCell = () => {
  const record = useRecordContext<Claimant>();

  const openTasks = record?.open_tasks;

  if (!openTasks) return <span>-</span>;

  return <span>{openTasks}</span>;
};

/** Displays work status from policy_data.status */
export const WorkStatusCell = () => {
  const { policyData } = useCurrentPolicyData();

  const isActivelyWorking = policyData?.status?.is_actively_working;

  if (isActivelyWorking === undefined || isActivelyWorking === null) return <span>-</span>;

  return <span>{isActivelyWorking ? "Active" : "Not Active"}</span>;
};
